// Port of src/app/services/spotify/spotify-playlist.service.ts.
// localStorage cache keys ('spotifyPlaylists', 'spotifyTracks', 'trackQueue')
// identical to the Angular app. NOTE: on error, createSpotifyPlaylist RESOLVES
// with `{ error: '...' }` (Angular's catchError → handleError) — it does not
// reject. Callers check `result.error`.

import { http } from '../http';
import type { PlaylistCreationResponse, TrackSearchResponse } from '../../interfaces/spotify';
import type { PlaylistReturnObject, SongsNotFound } from '../../interfaces/playlist-return-object';
import { refreshAccessToken, getBaseHeaders, handleError } from './spotify-base';

const SPOTIFY_SEARCH_URL = 'https://api.spotify.com/v1/search';
const SPOTIFY_PLAYLIST_URL = 'https://api.spotify.com/v1/me/playlists';
const SPOTIFY_PLAYLIST_ITEMS_URL = (playlistId: string) => `https://api.spotify.com/v1/playlists/${playlistId}/items`;
const THREE_DAYS_IN_MS = 3 * 24 * 60 * 60 * 1000; // 3 days in milliseconds

export async function createSpotifyPlaylist(
  songs: { artist: string; title: string }[],
  playlistName: string,
  playlistId?: string
): Promise<PlaylistReturnObject> {
  try {
    const accessToken = await refreshAccessToken();
    const trackData = await searchForTracks(songs, accessToken);

    const trackIds = trackData.map(data => data.id).filter(id => id);
    const songsNotFound: SongsNotFound[] = trackData.filter(data => !data.id).map(data => data.song);

    if (playlistId && isPlaylistCached(playlistId)) {
      const response = await updatePlaylistTracks(playlistId, trackIds);
      return { ...response, songsNotFound: songsNotFound };
    } else {
      const response = await createPlaylistAndAddTracks(trackIds, playlistName);
      return { ...response, songsNotFound: songsNotFound };
    }
  } catch (error) {
    return handleError(error);
  }
}

function isPlaylistCached(playlistId: string): boolean {
  const cachedPlaylists = JSON.parse(localStorage.getItem('spotifyPlaylists') || '[]');
  return cachedPlaylists.includes(playlistId);
}

async function addTracksToPlaylist(playlistId: string, trackIds: string[], accessToken: string): Promise<void> {
  const url = SPOTIFY_PLAYLIST_ITEMS_URL(playlistId);
  const body = { uris: trackIds.filter(id => id).map(id => `spotify:track:${id}`) };
  await http.post<void>(url, body, { headers: getBaseHeaders(accessToken) });
}

async function clearPlaylistTracks(playlistId: string, accessToken: string): Promise<void> {
  const url = SPOTIFY_PLAYLIST_ITEMS_URL(playlistId);
  const res = await http.get<{ items: { item: { uri: string } }[] }>(url, { headers: getBaseHeaders(accessToken) });

  const trackUris = res.data.items.map(entry => entry.item.uri);
  const body = {
    items: trackUris.map(uri => ({ uri }))
  };
  await http.delete<void>(url, { headers: getBaseHeaders(accessToken), data: body });
}

async function updatePlaylistTracks(playlistId: string, trackIds: string[]): Promise<PlaylistReturnObject> {
  const accessToken = await refreshAccessToken();
  try {
    // First, clear the existing tracks from the playlist
    await clearPlaylistTracks(playlistId, accessToken);
    // Then, add the new tracks to the playlist
    await addTracksToPlaylist(playlistId, trackIds, accessToken);
    // Return the playlist link
    return { link: `https://open.spotify.com/playlist/${playlistId}`, playlistId: playlistId };
  } catch (error) {
    return handleError(error);
  }
}

function getTracksCache(): { [key: string]: { id: string; timestamp: number } } {
  return JSON.parse(localStorage.getItem('spotifyTracks') || '{}');
}

function setTracksCache(tracks: { [key: string]: { id: string; timestamp: number } }): void {
  localStorage.setItem('spotifyTracks', JSON.stringify(tracks));
}

function getTrackQueue(): string[] {
  return JSON.parse(localStorage.getItem('trackQueue') || '[]');
}

function setTrackQueue(queue: string[]): void {
  localStorage.setItem('trackQueue', JSON.stringify(queue));
}

// NOTE(port): defined but never invoked in the Angular service either — kept for parity.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function cleanupExpiredTracks(): void {
  const tracks = getTracksCache();
  const queue = getTrackQueue();
  const now = Date.now();

  while (queue.length && tracks[queue[0]] && now - tracks[queue[0]].timestamp > THREE_DAYS_IN_MS) {
    const expiredTrack = queue.shift();
    if (expiredTrack && tracks[expiredTrack]) {
      delete tracks[expiredTrack];
    }
  }

  setTracksCache(tracks);
  setTrackQueue(queue);
}

function cleanString(input: string): string {
  // Remove text inside parentheses
  let cleaned = input.replace(/\(.*?\)/g, '').trim();
  // Remove special characters
  cleaned = cleaned.replace(/[^a-zA-Z0-9\s]/g, '');
  return cleaned;
}

function searchForTracks(songs: { artist: string; title: string }[], accessToken: string): Promise<{
  id: string;
  song: { artist: string; title: string };
}[]> {
  const trackPromises = songs.map(async song => {
    const cacheKey = `${song.artist} - ${song.title}`;
    const cachedTrack = getTracksCache()[cacheKey];

    if (cachedTrack) {
      return { id: cachedTrack.id, song };
    }

    const searchWithCleanedStrings = async () => {
      const cleanedArtist = cleanString(song.artist);
      const cleanedTitle = cleanString(song.title);
      const res = await http.get<TrackSearchResponse>(
        `${SPOTIFY_SEARCH_URL}?q=artist:${encodeURIComponent(cleanedArtist)}%20track:${encodeURIComponent(cleanedTitle)}&type=track&limit=10`,
        { headers: getBaseHeaders(accessToken) });

      if (res.data.tracks && res.data.tracks.items.length) {
        const trackId = res.data.tracks.items[0].id;
        return { id: trackId, song };
      } else {
        // Return the song with an empty ID if not found
        return { id: '', song };
      }
    };

    const res = await http.get<TrackSearchResponse>(
      `${SPOTIFY_SEARCH_URL}?q=artist:${encodeURIComponent(song.artist)}%20track:${encodeURIComponent(song.title)}&type=track&limit=10`,
      { headers: getBaseHeaders(accessToken) });

    if (res.data.tracks && res.data.tracks.items.length) {
      const trackId = res.data.tracks.items[0].id;
      return { id: trackId, song };
    } else {
      // Retry with cleaned strings if no tracks are found
      return searchWithCleanedStrings();
    }
  });

  // forkJoin → Promise.all
  return Promise.all(trackPromises);
}

function cachePlaylist(playlistId: string): void {
  const playlists = JSON.parse(localStorage.getItem('spotifyPlaylists') || '[]');
  if (!playlists.includes(playlistId)) {
    playlists.push(playlistId);
    localStorage.setItem('spotifyPlaylists', JSON.stringify(playlists));
  }
}

async function createPlaylistAndAddTracks(trackIds: string[], playlistName: string): Promise<PlaylistReturnObject> {
  const accessToken = await refreshAccessToken();

  const playlistRes = await http.post<PlaylistCreationResponse>(
    SPOTIFY_PLAYLIST_URL,
    { name: playlistName, public: true },
    { headers: getBaseHeaders(accessToken) }
  );
  const playlist = playlistRes.data;
  cachePlaylist(playlist.id);

  try {
    await http.post(
      SPOTIFY_PLAYLIST_ITEMS_URL(playlist.id),
      { uris: trackIds.filter(id => id).map(id => `spotify:track:${id}`) },
      { headers: getBaseHeaders(accessToken) }
    );
    return { link: `https://open.spotify.com/playlist/${playlist.id}`, playlistId: playlist.id }; // <-- Return the playlist ID here
  } catch (error) {
    return handleError(error);
  }
}
